import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, contactsAPI } from '../services/api';
import './MyData.css';

const MyData: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Contact sync state
  const [contactsCount, setContactsCount] = useState<number | null>(null);
  const [contactSyncEnabled, setContactSyncEnabled] = useState(() =>
    localStorage.getItem('contactSyncEnabled') !== 'false'
  );
  const [deletingContacts, setDeletingContacts] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Load contact sync status
    const loadContactStatus = async () => {
      try {
        const status = await contactsAPI.getStatus();
        if (status.synced) {
          setContactsCount(status.contactCount);
        }
      } catch (error) {
        console.error('Failed to load contact status:', error);
      }
    };
    loadContactStatus();
  }, []);

  const handleToggleContactSync = () => {
    const newValue = !contactSyncEnabled;
    setContactSyncEnabled(newValue);
    localStorage.setItem('contactSyncEnabled', newValue.toString());
    if (!newValue) {
      // Clear session flag so prompt shows again when re-enabled
      sessionStorage.removeItem('hideSyncStatus');
    }
  };

  const handleDeleteContacts = async () => {
    if (!window.confirm(t('myData.contactSync.deleteConfirm'))) {
      return;
    }
    setDeletingContacts(true);
    try {
      await contactsAPI.clear();
      setContactsCount(null);
      sessionStorage.removeItem('hideSyncStatus');
      setSuccess(t('myData.contactSync.deleteSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to delete contacts:', err);
      setError(t('myData.contactSync.deleteError'));
    } finally {
      setDeletingContacts(false);
    }
  };

  // Delete account confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  /**
   * Export all user data as JSON
   */
  const handleExportData = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await userAPI.exportUserData();

      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bobmap-data-${user?.email}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess(t('myData.downloadSuccess'));
    } catch (err: any) {
      setError(err.response?.data?.error || t('myData.downloadError'));
    } finally {
      setLoading(false);
    }
  };

  // Confirmation phrase based on language
  const confirmPhrase = language === 'fr' ? 'SUPPRIMER MON COMPTE' : 'DELETE MY ACCOUNT';

  /**
   * Delete account permanently
   */
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== confirmPhrase) {
      setError(t('myData.deleteConfirmError'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await userAPI.deleteAccount();
      setSuccess(t('myData.deleteSuccess'));

      // Logout and redirect to home
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('myData.deleteError'));
      setLoading(false);
    }
  };

  return (
    <div className="my-data-container">
      <div className="my-data-content">
        <h1>🔒 {t('myData.title')}</h1>
        <p className="subtitle">{t('myData.subtitle')}</p>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Current User Info */}
        <section className="data-section">
          <h2>👤 {t('myData.currentAccount')}</h2>
          <div className="user-info">
            <p>
              <strong>{t('profile.lastName')} :</strong> {user?.first_name} {user?.last_name}
            </p>
            <p>
              <strong>Email :</strong> {user?.email}
            </p>
            <p>
              <strong>{t('profile.phone')} :</strong> {user?.phone || t('myData.phoneNotProvided')}
            </p>
          </div>
        </section>

        {/* Export Data */}
        <section className="data-section">
          <div className="section-header">
            <div>
              <h2>📥 {t('myData.downloadTitle')}</h2>
              <p className="section-description">
                {t('myData.downloadDescription')}
              </p>
            </div>
            <button
              className="btn-export"
              onClick={handleExportData}
              disabled={loading}
            >
              {loading ? `⏳ ${t('myData.downloading')}` : `📥 ${t('myData.downloadButton')}`}
            </button>
          </div>

          <div className="data-included">
            <h3>{t('myData.dataIncluded')}</h3>
            <ul>
              <li>{t('myData.dataItems.profile')}</li>
              <li>{t('myData.dataItems.addresses')}</li>
              <li>{t('myData.dataItems.friends')}</li>
              <li>{t('myData.dataItems.groups')}</li>
              <li>{t('myData.dataItems.tempLocations')}</li>
              <li>{t('myData.dataItems.notificationPrefs')}</li>
            </ul>
            <p className="note" dangerouslySetInnerHTML={{ __html: `ℹ️ ${t('myData.passwordNote')}` }} />
          </div>
        </section>

        {/* Contacts Sync */}
        <section className="data-section contacts-section">
          <h2>📱 {t('myData.contactSync.title')}</h2>

          <div className="setting-row">
            <div className="setting-info">
              <h3>{t('myData.contactSync.enableTitle')}</h3>
              <p className="setting-description">
                {t('myData.contactSync.enableDescription')}
              </p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={contactSyncEnabled}
                onChange={handleToggleContactSync}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {contactsCount !== null && (
            <div className="setting-row contacts-data">
              <div className="setting-info">
                <h3>{t('myData.contactSync.syncedTitle')}</h3>
                <p className="setting-description">
                  {t('myData.contactSync.syncedDescription').replace('{count}', String(contactsCount))}
                </p>
              </div>
              <button
                className="btn-delete-contacts"
                onClick={handleDeleteContacts}
                disabled={deletingContacts}
              >
                {deletingContacts ? t('myData.contactSync.deleting') : t('myData.contactSync.deleteButton')}
              </button>
            </div>
          )}
        </section>

        {/* Delete Account */}
        <section className="data-section delete-section">
          <div className="section-header">
            <div>
              <h2>🗑️ {t('myData.deleteTitle')}</h2>
              <p className="section-description danger">
                {t('myData.deleteDescription')}
              </p>
            </div>
            <button
              className="btn-delete"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={loading}
            >
              🗑️ {t('myData.deleteButton')}
            </button>
          </div>

          {showDeleteConfirm && (
            <div className="delete-confirm-modal">
              <div className="modal-content">
                <h3>⚠️ {t('myData.deleteConfirmTitle')}</h3>
                <p className="warning-text" dangerouslySetInnerHTML={{ __html: t('myData.deleteWarnings.intro') }} />
                <ul className="delete-list">
                  <li>✗ {t('myData.deleteWarnings.profile')}</li>
                  <li>✗ {t('myData.deleteWarnings.addresses')}</li>
                  <li>✗ {t('myData.deleteWarnings.friends')}</li>
                  <li>✗ {t('myData.deleteWarnings.tempLocations')}</li>
                  <li>✗ {t('myData.deleteWarnings.groups')}</li>
                </ul>

                <div className="confirmation-input">
                  <label htmlFor="delete-confirm" dangerouslySetInnerHTML={{ __html: t('myData.deleteConfirmLabel') }} />
                  <input
                    type="text"
                    id="delete-confirm"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={confirmPhrase}
                    autoComplete="off"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmation('');
                      setError('');
                    }}
                    disabled={loading}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="btn-confirm-delete"
                    onClick={handleDeleteAccount}
                    disabled={loading || deleteConfirmation !== confirmPhrase}
                  >
                    {loading ? `⏳ ${t('myData.deleting')}` : `🗑️ ${t('myData.deleteButtonConfirm')}`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Legal Links */}
        <section className="data-section legal-links">
          <h2>📚 {t('myData.legalDocs')}</h2>
          <div className="links-grid">
            <Link to="/privacy-policy" className="legal-card">
              <h3>🔒 {t('myData.legalDocsItems.privacyTitle')}</h3>
              <p>{t('myData.legalDocsItems.privacyDesc')}</p>
            </Link>
            <Link to="/terms" className="legal-card">
              <h3>📜 {t('myData.legalDocsItems.termsTitle')}</h3>
              <p>{t('myData.legalDocsItems.termsDesc')}</p>
            </Link>
            <Link to="/legal" className="legal-card">
              <h3>⚖️ {t('myData.legalDocsItems.legalTitle')}</h3>
              <p>{t('myData.legalDocsItems.legalDesc')}</p>
            </Link>
          </div>
        </section>

        {/* RGPD Rights */}
        <section className="data-section rights-section">
          <h2>✊ {t('myData.yourRights')}</h2>
          <div className="rights-grid">
            <div className="right-card">
              <h3>👁️ {t('myData.rights.accessTitle')}</h3>
              <p>{t('myData.rights.accessDesc')}</p>
            </div>
            <div className="right-card">
              <h3>✏️ {t('myData.rights.rectificationTitle')}</h3>
              <p>{t('myData.rights.rectificationDesc')}</p>
            </div>
            <div className="right-card">
              <h3>🗑️ {t('myData.rights.erasureTitle')}</h3>
              <p>{t('myData.rights.erasureDesc')}</p>
            </div>
            <div className="right-card">
              <h3>📦 {t('myData.rights.portabilityTitle')}</h3>
              <p>{t('myData.rights.portabilityDesc')}</p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="data-section contact-section">
          <h2>📧 {t('myData.contact.title')}</h2>
          <p>
            {t('myData.contact.intro')}
          </p>
          <p className="contact-info">
            <strong>{t('myData.contact.email')} :</strong>{' '}
            <a href="mailto:bobmap.contact@gmail.com">bobmap.contact@gmail.com</a>
            <br />
            <strong>{t('myData.contact.address')} :</strong> 9 Allée de la Fauvette, 37100 Tours, France
          </p>
        </section>
      </div>
    </div>
  );
};

export default MyData;
